import { useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Building2, Clock, Mail, MapPin, MessageCircle, Phone, Send, UploadCloud, X } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs, AmbientOrb } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { toast } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { ADDRESS_LINES, EMAIL, MAP_EMBED_SRC, PHONE, WHATSAPP_HREF } from "@/lib/data";
import SEO from "@/components/SEO";
import { getOrganizationSchema } from "@/lib/seoSchemas";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const SERVICES = [
  "Private Limited Company",
  "LLP Registration",
  "OPC Registration",
  "Partnership Firm",
  "Sole Proprietorship",
  "Section 8 Company",
  "Producer Company",
  "Trust / NGO Registration",
  "GST & Taxation",
  "Accounting & Bookkeeping",
  "Other",
];

const contactSchema = z.object({
  name: z.string().trim().min(2, "Please enter your full name"),
  email: z.string().trim().email("Enter a valid email address"),
  phone: z
    .string()
    .trim()
    .min(10, "Enter a valid phone number")
    .regex(/^[0-9+\-\s]+$/, "Enter a valid phone number"),
  state: z.string().min(1, "Please select your state"),
  service: z.string().min(1, "Please select a service"),
  message: z.string().trim().min(10, "Tell us a little more (min. 10 characters)"),
});

const FIELD_CLASS =
  "w-full rounded-xl border border-navy-900/15 bg-white px-4 py-3 text-sm text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20";

const PUNE_OFFICE = {
  city: "Pune",
  title: "Pune Head Office",
  badge: "Headquarters",
  lines: [
    "Office No. 70, 4th Floor, Patil Arcade",
    "Above Hotel Kalinga, Khilare Road",
    "Near Sharda Center, Erandwane",
    "Pune, Maharashtra – 411004",
  ],
  mapEmbedUrl: MAP_EMBED_SRC,
  mapLink: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Office No. 70, Patil Arcade, Above Hotel Kalinga, Khilare Road, Near Sharda Center, Erandwane, Pune, Maharashtra 411004")}`,
};

const PARBHANI_OFFICE = {
  city: "Parbhani",
  title: "Parbhani Branch Office",
  badge: "Branch Office",
  lines: [
    "Office No. 101, First Floor, Mundhe Building",
    "Near State Bank of India (SBI), Shivram Nagar",
    "Vasmat Road, Parbhani",
    "Maharashtra – 431401, India",
  ],
  mapEmbedUrl: `https://maps.google.com/maps?q=${encodeURIComponent("Office No. 101, First Floor, Mundhe Building, Near State Bank of India (SBI), Shivram Nagar, Vasmat Road, Parbhani, Maharashtra 431401")}&t=&z=15&ie=UTF8&iwloc=&output=embed`,
  mapLink: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Office No. 101, First Floor, Mundhe Building, Near State Bank of India (SBI), Shivram Nagar, Vasmat Road, Parbhani, Maharashtra 431401")}`,
};

const CONTACT_CARDS = [
  {
    icon: Building2,
    badge: "Headquarters",
    title: "Pune Head Office",
    lines: [
      "Office No. 70, Patil Arcade",
      "Above Hotel Kalinga, Erandwane",
      "Pune, Maharashtra – 411004",
    ],
    action: { label: "Get Directions", href: PUNE_OFFICE.mapLink },
  },
  {
    icon: MapPin,
    badge: "Branch Office",
    title: "Parbhani Branch",
    lines: [
      "Office No. 101, 1st Floor, Mundhe Bldg, Near SBI, Vasmat Road, Parbhani, Maharashtra – 431401",
    ],
    action: { label: "Get Directions", href: PARBHANI_OFFICE.mapLink },
  },
  {
    icon: Phone,
    badge: "Direct Support",
    title: "Call & Email",
    lines: [PHONE, EMAIL],
    action: { label: "Call Us", href: `tel:${PHONE.replace(/\s+/g, "")}` },
  },
  {
    icon: Clock,
    badge: "Open 6 Days",
    title: "Working Hours",
    lines: ["Monday – Saturday", "9:30 AM – 7:00 PM IST", "Online & Physical Consultations"],
    action: { label: "WhatsApp", href: WHATSAPP_HREF },
  },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [uploadHighlight, setUploadHighlight] = useState(false);
  const [activeMapTab, setActiveMapTab] = useState("pune");
  const uploadSectionRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(contactSchema), defaultValues: { name: "", email: "", phone: "", state: "", service: "", message: "" } });

  useEffect(() => {
    const serviceParam = searchParams.get("service");
    if (serviceParam && SERVICES.includes(serviceParam)) {
      setValue("service", serviceParam);
    }
    if (searchParams.get("intent") === "upload") {
      uploadSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setUploadHighlight(true);
      const timeout = setTimeout(() => setUploadHighlight(false), 2200);
      return () => clearTimeout(timeout);
    }

    const isContactFormTarget =
      location.hash === "#contact-form" ||
      location.hash === "#enquiry" ||
      location.hash === "#enquiry-form" ||
      window.location.hash.includes("contact-form") ||
      window.location.hash.includes("enquiry") ||
      sessionStorage.getItem("scrollToContactForm") === "true" ||
      searchParams.get("intent") === "register" ||
      searchParams.get("section") === "contact-form";

    if (isContactFormTarget) {
      try {
        sessionStorage.removeItem("scrollToContactForm");
      } catch (_) {}

      const performScroll = () => {
        const el = document.getElementById("contact-form") || document.getElementById("enquiry-form");
        if (el) {
          if (window.__lenis) {
            window.__lenis.scrollTo(el, { offset: -90, duration: 1.2 });
          } else {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      };

      performScroll();
      const t1 = setTimeout(performScroll, 150);
      const t2 = setTimeout(performScroll, 400);
      const t3 = setTimeout(performScroll, 800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    } else if (window.location.hash) {
      const rawId = window.location.hash.replace("#", "");
      const timer = setTimeout(() => {
        const target = document.getElementById(rawId);
        if (target) {
          if (window.__lenis) {
            window.__lenis.scrollTo(target, { offset: -90, duration: 1.2 });
          } else {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }, 180);
      return () => clearTimeout(timer);
    }
  }, [searchParams, location.hash, setValue]);

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const onSubmit = async (values) => {
    const body = new FormData();
    Object.entries(values).forEach(([key, value]) => body.append(key, value));
    files.forEach((file) => body.append("attachments", file));

    try {
      await api.sendEnquiry(body);
      toast.success("Message sent — we'll get back to you within one business day.", {
        description: `Thanks, ${values.name.split(" ")[0]}! ${
          files.length > 0 ? `${files.length} document${files.length > 1 ? "s" : ""} attached — ` : ""
        }We've emailed you a copy of your enquiry.`,
      });
      reset();
      setFiles([]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="contact-page">
      <SEO
        title="Contact Biz360 Compliance | Pune & Parbhani Offices, Maharashtra"
        description="Contact Biz360 Compliance at our Pune Headquarters or Parbhani Branch Office. Expert CA, CS and legal consultation for company registration, GST filing, and compliance across India."
        canonical="/contact"
        keywords="contact Biz360 Pune, Biz360 Parbhani, Mundhe Building Vasmat Road, Patil Arcade Erandwane, company registration office Maharashtra, tax consultant helpline"
        breadcrumbs={[{ label: "Contact Us", href: "/contact" }]}
        schema={getOrganizationSchema()}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <Reveal>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Get in Touch
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">Let's talk about your business</h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-4 text-base text-slate-600 md:text-lg">
                  Whether you're registering a new entity or need ongoing compliance support, our team is a message away.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {CONTACT_CARDS.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.05}>
                  <div
                    data-testid={`contact-card-${i}`}
                    className="group flex h-full flex-col justify-between rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold-400/40 hover:shadow-[0_16px_36px_rgba(15,30,60,0.1)]"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-navy-900 text-gold-400 transition-colors group-hover:bg-gold-500 group-hover:text-navy-950">
                          <c.icon className="h-5 w-5" />
                        </span>
                        {c.badge && (
                          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                            {c.badge}
                          </span>
                        )}
                      </div>
                      <h3 className="mt-4 text-sm font-bold text-navy-900">{c.title}</h3>
                      <div className="mt-2 space-y-0.5 text-sm text-slate-500">
                        {c.lines.map((l) => {
                          if (l === EMAIL) {
                            return (
                              <p key={l}>
                                <a
                                  href="#contact-form"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }}
                                  className="text-gold-600 transition-colors hover:text-gold-700 hover:underline"
                                >
                                  {l}
                                </a>
                              </p>
                            );
                          }
                          if (l === PHONE) {
                            return (
                              <p key={l}>
                                <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="hover:text-gold-600 transition-colors">
                                  {l}
                                </a>
                              </p>
                            );
                          }
                          return <p key={l}>{l}</p>;
                        })}
                      </div>
                    </div>
                    {c.action && (
                      <div className="mt-4 border-t border-slate-100 pt-3">
                        <a
                          href={c.action.href}
                          target={c.action.href.startsWith("http") ? "_blank" : undefined}
                          rel={c.action.href.startsWith("http") ? "noreferrer" : undefined}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gold-600 transition-colors hover:text-gold-700 group-hover:underline"
                        >
                          {c.action.label} <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr]">
              {/* Form */}
              <Reveal>
                <div id="contact-form" className="relative scroll-mt-28 rounded-3xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-6 md:p-10" data-testid="contact-form-card">
                  <span id="enquiry" className="absolute -top-28 left-0 pointer-events-none" />
                  <h2 className="text-2xl font-semibold text-navy-900">Send us a message</h2>
                  <p className="mt-2 text-sm text-slate-600">Fill in your details and our team will get back to you within one business day.</p>

                  <form id="enquiry-form" onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" data-testid="contact-form" noValidate>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="mb-1.5 block text-sm font-semibold text-navy-800">
                          Full Name
                        </label>
                        <input id="name" type="text" placeholder="Ananya Sharma" className={FIELD_CLASS} data-testid="contact-input-name" {...register("name")} />
                        {errors.name && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.name.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="phone" className="mb-1.5 block text-sm font-semibold text-navy-800">
                          Phone Number
                        </label>
                        <input id="phone" type="tel" placeholder="+91 98765 43210" className={FIELD_CLASS} data-testid="contact-input-phone" {...register("phone")} />
                        {errors.phone && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.phone.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-navy-800">
                        Email Address
                      </label>
                      <input id="email" type="email" placeholder="you@company.com" className={FIELD_CLASS} data-testid="contact-input-email" {...register("email")} />
                      {errors.email && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email.message}</p>}
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="state" className="mb-1.5 block text-sm font-semibold text-navy-800">
                          State
                        </label>
                        <select id="state" className={`${FIELD_CLASS} appearance-none`} data-testid="contact-input-state" defaultValue="" {...register("state")}>
                          <option value="" disabled>
                            Select your state
                          </option>
                          {INDIAN_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {errors.state && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.state.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="service" className="mb-1.5 block text-sm font-semibold text-navy-800">
                          Service You Need
                        </label>
                        <select id="service" className={`${FIELD_CLASS} appearance-none`} data-testid="contact-input-service" defaultValue="" {...register("service")}>
                          <option value="" disabled>
                            Select a service
                          </option>
                          {SERVICES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {errors.service && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.service.message}</p>}
                      </div>
                    </div>

                    <div
                      ref={uploadSectionRef}
                      className={`rounded-2xl transition-shadow duration-500 ${uploadHighlight ? "ring-2 ring-gold-400 ring-offset-2 ring-offset-[rgb(238,242,250)]" : ""}`}
                    >
                      <label className="mb-1.5 block text-sm font-semibold text-navy-800">
                        Attach Documents <span className="font-normal text-slate-400">(optional)</span>
                      </label>
                      <label
                        htmlFor="documents"
                        data-testid="contact-upload-dropzone"
                        className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-navy-900/15 bg-white px-4 py-6 text-center transition-colors hover:border-gold-400"
                      >
                        <UploadCloud className="h-5 w-5 text-navy-400" />
                        <span className="text-sm font-medium text-navy-700">Click to upload or drag files here</span>
                        <span className="text-xs text-slate-400">PDF, JPG or PNG — up to 10MB each</span>
                        <input
                          id="documents"
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          data-testid="contact-upload-input"
                          onChange={handleFilesSelected}
                        />
                      </label>
                      {files.length > 0 && (
                        <ul className="mt-3 space-y-1.5" data-testid="contact-upload-list">
                          {files.map((f, i) => (
                            <li
                              key={`${f.name}-${i}`}
                              className="flex items-center justify-between rounded-lg bg-navy-50 px-3 py-2 text-xs text-navy-700"
                            >
                              <span className="truncate">{f.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(i)}
                                aria-label={`Remove ${f.name}`}
                                className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div>
                      <label htmlFor="message" className="mb-1.5 block text-sm font-semibold text-navy-800">
                        Message
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        placeholder="Tell us a bit about your business and what you need help with..."
                        className={FIELD_CLASS}
                        data-testid="contact-input-message"
                        {...register("message")}
                      />
                      {errors.message && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.message.message}</p>}
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileTap={{ scale: 0.98 }}
                      data-testid="contact-submit"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold-500 px-7 py-3.5 text-sm font-semibold text-navy-950 shadow-[0_12px_30px_rgba(212,162,76,0.35)] transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60 sm:w-auto"
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                      <Send className="h-4 w-4" />
                    </motion.button>
                  </form>
                </div>
              </Reveal>

              {/* Side panel */}
              <div className="space-y-6">
                <Reveal delay={0.05}>
                  <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-navy-900 p-8 text-white">
                    <AmbientOrb className="-right-16 -top-16" size="h-64 w-64" blur="blur-3xl" color="bg-gold-500/20" duration={10} />
                    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald2-500/20 text-emerald2-400">
                      <MessageCircle className="h-5 w-5" />
                    </span>
                    <h3 className="relative mt-5 text-lg font-semibold">Prefer to chat instantly?</h3>
                    <p className="relative mt-2 text-sm leading-relaxed text-white/65">
                      Message us on WhatsApp for a quick response, or call our team directly during working hours.
                    </p>
                    <div className="relative mt-6 flex flex-wrap gap-3">
                      <a
                        href={WHATSAPP_HREF}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-whatsapp-link"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald2-500 px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
                      >
                        WhatsApp Us <ArrowRight className="h-4 w-4" />
                      </a>
                      <a
                        href={`tel:${PHONE.replace(/\s+/g, "")}`}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-gold-400 hover:text-gold-400"
                      >
                        <Phone className="h-4 w-4" /> Call Now
                      </a>
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={0.1}>
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" data-testid="contact-map">
                    {/* Header with office switcher */}
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Our Physical Offices</span>
                      </div>
                      <div className="inline-flex rounded-xl bg-slate-200/80 p-1">
                        <button
                          type="button"
                          onClick={() => setActiveMapTab("pune")}
                          className={`rounded-lg px-3 py-1 text-xs font-semibold transition-all ${
                            activeMapTab === "pune"
                              ? "bg-white text-navy-950 shadow-sm"
                              : "text-slate-600 hover:text-navy-900"
                          }`}
                        >
                          Pune (HQ)
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveMapTab("parbhani")}
                          className={`rounded-lg px-3 py-1 text-xs font-semibold transition-all ${
                            activeMapTab === "parbhani"
                              ? "bg-white text-navy-950 shadow-sm"
                              : "text-slate-600 hover:text-navy-900"
                          }`}
                        >
                          Parbhani (Branch)
                        </button>
                      </div>
                    </div>

                    {/* Google Map Embed */}
                    <iframe
                      key={activeMapTab}
                      title={`Biz360 Compliance ${activeMapTab === "pune" ? "Pune" : "Parbhani"} office location`}
                      src={activeMapTab === "pune" ? PUNE_OFFICE.mapEmbedUrl : PARBHANI_OFFICE.mapEmbedUrl}
                      className="h-64 w-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />

                    {/* Office Addresses List */}
                    <div className="divide-y divide-slate-100 p-5 bg-white space-y-4">
                      {/* Pune Office */}
                      <div className={`transition-opacity ${activeMapTab === "pune" ? "opacity-100" : "opacity-80"}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-bold text-navy-900">Pune (Headquarters)</h4>
                              <span className="rounded-full bg-gold-400/15 px-2 py-0.5 text-[10px] font-bold text-gold-700">HQ</span>
                            </div>
                            <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                              Office No. 70, 4th Floor, Patil Arcade, Above Hotel Kalinga, Khilare Road, Near Sharda Center, Erandwane, Pune, Maharashtra – 411004
                            </p>
                          </div>
                          <a
                            href={PUNE_OFFICE.mapLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-navy-900 transition-colors hover:border-gold-400 hover:bg-gold-50"
                          >
                            Directions <ArrowUpRight className="h-3 w-3" />
                          </a>
                        </div>
                      </div>

                      {/* Parbhani Office */}
                      <div className={`pt-4 transition-opacity ${activeMapTab === "parbhani" ? "opacity-100" : "opacity-80"}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-bold text-navy-900">Parbhani (Branch Office)</h4>
                              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Branch</span>
                            </div>
                            <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                              Office No. 101, First Floor, Mundhe Building, Near State Bank of India (SBI), Shivram Nagar, Vasmat Road, Parbhani, Maharashtra – 431401, India
                            </p>
                          </div>
                          <a
                            href={PARBHANI_OFFICE.mapLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-navy-900 transition-colors hover:border-gold-400 hover:bg-gold-50"
                          >
                            Directions <ArrowUpRight className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
